/**
 * Onglet admin : traitement des réclamations portail.
 * KPIs + filtres pôle/niveau/classe + chat de conversation + clôture.
 */
import React, { useState, useMemo, useEffect } from 'react';
import {
  useListReclamationsAdminQuery,
  useTraiterReclamationMutation,
  useGetReclamationMessagesQuery,
  useSendReclamationMessageMutation,
  useCloturerReclamationMutation,
  useRouvrirReclamationMutation,
  useGetReclamationQuickEditQuery,
  useApplyReclamationQuickEditMutation,
  ReclamationAdminItem,
} from '../../services/portailAdminApi';

const STATUTS = [
  { key: '', label: 'Tous' },
  { key: 'deposee', label: 'Déposées' },
  { key: 'en_examen', label: 'En examen' },
  { key: 'acceptee', label: 'Acceptées' },
  { key: 'rejetee', label: 'Rejetées' },
  { key: 'cloturee', label: 'Clôturées' },
];

const STATUT_BADGE: Record<string, string> = {
  deposee: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  en_examen: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  acceptee: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  rejetee: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  cloturee: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

const STATUT_LABEL: Record<string, string> = {
  deposee: 'Déposée',
  en_examen: 'En examen',
  acceptee: 'Acceptée',
  rejetee: 'Rejetée',
  cloturee: 'Clôturée',
};

function fmtDate(s: string): string {
  return new Date(s).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function fmtTime(s: string): string {
  return new Date(s).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'à l\'instant';
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h}h`;
  const j = Math.floor(h / 24);
  return `il y a ${j}j`;
}

const KpiCard: React.FC<{
  label: string;
  value: number;
  color: string;
  icon: string;
  active?: boolean;
  onClick?: () => void;
}> = ({ label, value, color, icon, active, onClick }) => (
  <button
    onClick={onClick}
    disabled={!onClick}
    className={`text-left p-3 rounded-lg border transition shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md' : ''} ${
      active
        ? `bg-${color}-100 dark:bg-${color}-900/30 border-${color}-400 ring-2 ring-${color}-300`
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
    }`}
  >
    <div className="flex items-center gap-2 mb-1">
      <span className="text-lg">{icon}</span>
      <div className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">{label}</div>
    </div>
    <div className={`text-2xl font-bold text-${color}-700 dark:text-${color}-400`}>{value}</div>
  </button>
);

// =====================================================================
// Modal Action Rapide : édition note + scan + clôture en 1 clic
// =====================================================================
const QuickEditModal: React.FC<{
  reclamationId: number;
  onClose: () => void;
  onApplied: () => void;
}> = ({ reclamationId, onClose, onApplied }) => {
  const { data, isFetching } = useGetReclamationQuickEditQuery(reclamationId);
  const [apply, { isLoading: applying }] = useApplyReclamationQuickEditMutation();

  const [n1, setN1] = useState('');
  const [n2, setN2] = useState('');
  const [absent1, setAbsent1] = useState(false);
  const [absent2, setAbsent2] = useState(false);
  const [nExam, setNExam] = useState('');
  const [absExam, setAbsExam] = useState(false);
  // Session de rattrapage (2026-10-05) : la note qui compte est la meilleure des deux
  const [nRatt, setNRatt] = useState('');
  const [absRatt, setAbsRatt] = useState(false);
  const [editDevoir, setEditDevoir] = useState(false);
  const [editExamen, setEditExamen] = useState(false);
  const [editRatt, setEditRatt] = useState(false);
  const [commentaire, setCommentaire] = useState('');
  const [showFullScan, setShowFullScan] = useState<string | null>(null);

  // Préremplir au chargement
  React.useEffect(() => {
    if (!data) return;
    const d = data.notes_actuelles.devoir;
    const e = data.notes_actuelles.examen;
    setN1(d.note1 ?? '');
    setN2(d.note2 ?? '');
    setAbsent1(d.absent1);
    setAbsent2(d.absent2);
    setNExam(e.note_examen ?? '');
    setAbsExam(!!e.absent_examen);
    const r = data.notes_actuelles.rattrapage;
    setNRatt(r.note_examen ?? '');
    setAbsRatt(!!r.absent_examen);
    // Pré-cocher la section selon le type de réclamation. Une réclamation
    // « examen » vise la note retenue : le rattrapage s'il l'emporte.
    const t = data.reclamation.type_reclamation;
    if (t === 'devoir') setEditDevoir(true);
    else if (t === 'rattrapage') setEditRatt(true);
    else if (t === 'examen') {
      if (data.notes_actuelles.note_retenue === 'SR') setEditRatt(true);
      else setEditExamen(true);
    }
  }, [data]);

  const submit = async () => {
    if (commentaire.trim().length < 5) {
      alert('Commentaire requis (5 caractères min) — il sera envoyé à l\'étudiant');
      return;
    }
    const body: any = { id: reclamationId, commentaire: commentaire.trim() };
    if (editDevoir) {
      body.note1 = n1.trim() === '' ? null : n1.trim();
      body.absent1 = absent1;
      body.note2 = n2.trim() === '' ? null : n2.trim();
      body.absent2 = absent2;
    }
    if (editExamen) {
      body.note_examen = absExam || nExam.trim() === '' ? null : nExam.trim();
      body.absent_examen = absExam;
    }
    if (editRatt) {
      body.note_rattrapage = absRatt || nRatt.trim() === '' ? null : nRatt.trim();
      body.absent_rattrapage = absRatt;
    }
    if (!editDevoir && !editExamen && !editRatt) {
      alert('Cochez au moins une section à modifier (devoir, session ordinaire ou rattrapage)');
      return;
    }
    try {
      const res = await apply(body).unwrap();
      const msg = res.changements.length > 0
        ? `✓ Réclamation traitée !\n\nModifications :\n• ${res.changements.join('\n• ')}`
        : '✓ Réclamation clôturée (aucun changement de note)';
      alert(msg);
      onApplied();
      onClose();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  if (isFetching || !data) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6">Chargement...</div>
      </div>
    );
  }

  const { reclamation: rec, etudiant, notes_actuelles, scans } = data;
  const scanDevoir = scans.find(s => s.type === 'devoir');
  const scanExamen = scans.find(s => s.type === 'examen');
  const scanRatt = scans.find(s => s.type === 'rattrapage');
  const retenue = notes_actuelles.note_retenue;

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-gray-900 dark:text-white">
              ⚡ Action rapide — {etudiant.nom_complet}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {rec.matiere_code} · {rec.matiere_nom} · {etudiant.classe} · {etudiant.matricule}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 text-2xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Motif de la réclamation */}
          <div className="bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded p-3">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Motif réclamation ({rec.type_reclamation})</div>
            <div className="text-sm text-gray-900 dark:text-gray-100 italic">« {rec.motif} »</div>
            {(rec.note_actuelle || rec.note_attendue) && (
              <div className="text-xs text-gray-500 mt-2">
                {rec.note_actuelle && <>Note actuelle déclarée : <strong>{rec.note_actuelle}</strong></>}
                {rec.note_actuelle && rec.note_attendue && ' → '}
                {rec.note_attendue && <>Attendue : <strong>{rec.note_attendue}</strong></>}
              </div>
            )}
          </div>

          {/* Devoir : scan + édition */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <label className={`flex items-center gap-2 p-3 cursor-pointer ${editDevoir ? 'bg-blue-50 dark:bg-blue-900/30' : 'bg-gray-50 dark:bg-gray-900/40'}`}>
              <input type="checkbox" checked={editDevoir} onChange={(e) => setEditDevoir(e.target.checked)} />
              <span className="font-semibold text-sm text-gray-900 dark:text-white">📝 Modifier les notes de devoir (D1 / D2)</span>
              {notes_actuelles.devoir.moyenne_devoirs && (
                <span className="ml-auto text-xs text-gray-500">
                  Moyenne actuelle : <strong>{notes_actuelles.devoir.moyenne_devoirs}/20</strong>
                </span>
              )}
            </label>

            {editDevoir && (
              <div className="p-3 space-y-3">
                {scanDevoir ? (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">📷 Scan du relevé devoirs (cliquer pour agrandir)</div>
                    <img
                      src={`data:image/jpeg;base64,${scanDevoir.image_base64}`}
                      alt="Scan devoir"
                      onClick={() => setShowFullScan(scanDevoir.image_base64)}
                      className="max-w-full h-auto max-h-32 border border-gray-300 dark:border-gray-600 rounded cursor-zoom-in"
                    />
                    <div className="text-[10px] text-gray-400 mt-1">{scanDevoir.code_releve}</div>
                  </div>
                ) : (
                  <div className="text-xs text-gray-400 italic">Aucun scan de devoir disponible pour cet étudiant</div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-700 dark:text-gray-300 mb-1">Devoir 1 (D1)</label>
                    <input
                      type="number" step="0.25" min="0" max="20"
                      value={n1}
                      onChange={(e) => setN1(e.target.value)}
                      disabled={absent1}
                      placeholder="0 - 20"
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                    />
                    <label className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                      <input type="checkbox" checked={absent1} onChange={(e) => setAbsent1(e.target.checked)} />
                      Absent
                    </label>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-700 dark:text-gray-300 mb-1">Devoir 2 (D2)</label>
                    <input
                      type="number" step="0.25" min="0" max="20"
                      value={n2}
                      onChange={(e) => setN2(e.target.value)}
                      disabled={absent2}
                      placeholder="0 - 20"
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                    />
                    <label className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                      <input type="checkbox" checked={absent2} onChange={(e) => setAbsent2(e.target.checked)} />
                      Absent
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Examens : session ordinaire et rattrapage (2026-10-05) */}
          <div className="text-xs text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 flex flex-wrap gap-x-4 gap-y-1">
            <span>
              Note d'examen retenue :{' '}
              <strong>
                {retenue === 'SR' ? `rattrapage (${notes_actuelles.rattrapage.note_examen}/20)`
                  : retenue === 'SO' ? `session ordinaire (${notes_actuelles.examen.note_examen}/20)`
                    : 'aucune'}
              </strong>
              <span className="text-gray-400"> — la meilleure des deux compte</span>
            </span>
            {notes_actuelles.moyenne_ecue && (
              <span>Moyenne de la matière : <strong>{notes_actuelles.moyenne_ecue}/20</strong></span>
            )}
          </div>
          {([
            {
              cle: 'SO', titre: "Examen — session ordinaire", actif: editExamen, setActif: setEditExamen,
              note: nExam, setNote: setNExam, abs: absExam, setAbs: setAbsExam,
              infos: notes_actuelles.examen, scan: scanExamen, couleur: 'purple',
              vide: "Aucune copie de session ordinaire scannée pour cet étudiant",
            },
            {
              cle: 'SR', titre: 'Examen — rattrapage', actif: editRatt, setActif: setEditRatt,
              note: nRatt, setNote: setNRatt, abs: absRatt, setAbs: setAbsRatt,
              infos: notes_actuelles.rattrapage, scan: scanRatt, couleur: 'orange',
              vide: 'Aucune copie de rattrapage scannée pour cet étudiant',
            },
          ] as const).map((sec) => (
            <div key={sec.cle} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <label className={`flex items-center gap-2 p-3 cursor-pointer ${
                sec.actif
                  ? (sec.couleur === 'orange' ? 'bg-orange-50 dark:bg-orange-900/30' : 'bg-purple-50 dark:bg-purple-900/30')
                  : 'bg-gray-50 dark:bg-gray-900/40'}`}>
                <input type="checkbox" checked={sec.actif} onChange={(e) => sec.setActif(e.target.checked)} />
                <span className="font-semibold text-sm text-gray-900 dark:text-white">{sec.titre}</span>
                {retenue === sec.cle && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">note retenue</span>
                )}
                <span className="ml-auto text-xs text-gray-500">
                  {sec.infos.existe
                    ? <>Note actuelle : <strong>{sec.infos.absent_examen ? 'absent' : `${sec.infos.note_examen ?? '—'}/20`}</strong></>
                    : 'Pas de note'}
                </span>
              </label>
              {sec.actif && (
                <div className="p-3 space-y-3">
                  {sec.scan ? (
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Copie scannée (cliquer pour agrandir)</div>
                      <img
                        src={`data:image/jpeg;base64,${sec.scan.image_base64}`}
                        alt={sec.titre}
                        onClick={() => setShowFullScan(sec.scan!.image_base64)}
                        className="max-w-full h-auto max-h-48 border border-gray-300 dark:border-gray-600 rounded cursor-zoom-in"
                      />
                      <div className="text-[10px] text-gray-400 mt-1">{sec.scan.code_releve}</div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 italic">{sec.vide}</div>
                  )}
                  {sec.infos.verrouille && (
                    <div className="text-xs text-amber-700 dark:text-amber-400">Note verrouillée : la modification sera tracée au nom de l'administration.</div>
                  )}
                  <div>
                    <label className="block text-xs text-gray-700 dark:text-gray-300 mb-1">Nouvelle note</label>
                    <input
                      type="number" step="0.25" min="0" max="20"
                      value={sec.note}
                      onChange={(e) => sec.setNote(e.target.value)}
                      disabled={sec.abs}
                      placeholder="0 - 20"
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                    />
                    <label className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                      <input type="checkbox" checked={sec.abs} onChange={(e) => sec.setAbs(e.target.checked)} />
                      Absent
                    </label>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Commentaire à l'étudiant */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              💬 Réponse à l'étudiant <span className="text-red-500">*</span> (visible dans son portail)
            </label>
            <textarea
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Ex: Erreur de saisie corrigée — la note D1 était 14, pas 4. Réclamation traitée."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <div className="text-[10px] text-gray-400 mt-0.5">{commentaire.length}/500</div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded p-2 text-xs text-blue-800 dark:text-blue-200">
            ℹ Au clic sur Valider : les notes seront mises à jour, les moyennes recalculées,
            la réclamation sera <strong>clôturée</strong>, et l'étudiant recevra une notification.
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 p-3 flex gap-2 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm rounded"
          >Annuler</button>
          <button
            onClick={submit}
            disabled={applying || commentaire.trim().length < 5 || (!editDevoir && !editExamen && !editRatt)}
            className="flex-1 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium rounded"
          >
            {applying ? 'Application...' : '✓ Valider et clôturer'}
          </button>
        </div>
      </div>

      {/* Image plein écran si clic sur scan */}
      {showFullScan && (
        <div
          className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-2 cursor-zoom-out"
          onClick={() => setShowFullScan(null)}
        >
          <img
            src={`data:image/jpeg;base64,${showFullScan}`}
            alt="Scan agrandi"
            className="max-w-full max-h-full"
          />
        </div>
      )}
    </div>
  );
};

const ConversationModal: React.FC<{
  reclamation: ReclamationAdminItem;
  onClose: () => void;
}> = ({ reclamation, onClose }) => {
  const { data, isFetching, refetch } = useGetReclamationMessagesQuery(reclamation.id);
  const [sendMsg, { isLoading: sending }] = useSendReclamationMessageMutation();
  const [cloturer, { isLoading: closing }] = useCloturerReclamationMutation();
  const [rouvrir, { isLoading: reopening }] = useRouvrirReclamationMutation();
  const [contenu, setContenu] = useState('');
  const isCloturee = data?.reclamation?.cloturee || reclamation.statut === 'cloturee';

  const submit = async () => {
    if (contenu.trim().length < 2) return;
    try {
      await sendMsg({ id: reclamation.id, contenu: contenu.trim() }).unwrap();
      setContenu('');
      refetch();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  const handleCloturer = async () => {
    const raison = window.prompt('Raison de la clôture (optionnel) :');
    if (raison === null) return;
    try {
      await cloturer({ id: reclamation.id, raison }).unwrap();
      refetch();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  const handleRouvrir = async () => {
    if (!window.confirm('Rouvrir cette réclamation clôturée ?')) return;
    try {
      await rouvrir({ id: reclamation.id }).unwrap();
      refetch();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 dark:text-white">
              💬 {reclamation.etudiant.nom} {reclamation.etudiant.prenoms}
              <span className="ml-2 text-xs font-mono text-gray-500">{reclamation.matricule}</span>
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {reclamation.matiere_nom || '—'} • {reclamation.etudiant.classe}
              {' • '}<span className={`px-2 py-0.5 rounded-full text-xs ${STATUT_BADGE[reclamation.statut]}`}>{STATUT_LABEL[reclamation.statut]}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 text-2xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900 space-y-3">
          {isFetching ? (
            <div className="text-center text-gray-400 text-sm py-8">Chargement...</div>
          ) : (
            (data?.results || []).map((m) => {
              const isMe = m.expediteur === 'scolarite';
              const isSys = m.expediteur === 'systeme';
              return (
                <div key={m.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                    isSys
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-900 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-800 italic'
                      : isMe
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-600'
                  }`}>
                    {!isSys && (
                      <div className={`text-[10px] font-semibold mb-0.5 ${isMe ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'}`}>
                        {m.expediteur_nom || (m.expediteur === 'etudiant' ? 'Étudiant' : 'Scolarité')}
                      </div>
                    )}
                    <div className="whitespace-pre-wrap break-words">{m.contenu}</div>
                    <div className={`text-[10px] mt-1 ${isMe ? 'text-blue-100' : 'text-gray-400'}`}>
                      {fmtTime(m.date_creation)} · {timeAgo(m.date_creation)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 p-3 space-y-2">
          {isCloturee ? (
            <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-900/40 rounded p-2 text-sm">
              <span className="text-gray-600 dark:text-gray-400">🔒 Conversation clôturée</span>
              <button
                onClick={handleRouvrir}
                disabled={reopening}
                className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
              >
                {reopening ? '…' : '🔓 Rouvrir'}
              </button>
            </div>
          ) : (
            <>
              <textarea
                value={contenu}
                onChange={(e) => setContenu(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit();
                }}
                rows={2}
                placeholder="Répondre à l'étudiant... (Ctrl+Entrée pour envoyer)"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <div className="flex gap-2">
                <button
                  onClick={submit}
                  disabled={sending || contenu.trim().length < 2}
                  className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded"
                >
                  {sending ? 'Envoi...' : '📨 Envoyer'}
                </button>
                <button
                  onClick={handleCloturer}
                  disabled={closing}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 text-gray-700 dark:text-gray-200 text-sm rounded"
                  title="Clôturer définitivement la réclamation"
                >
                  🔒 Clôturer
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ReclamationCard: React.FC<{
  r: ReclamationAdminItem;
  onTraiter: (action: 'en_examen' | 'acceptee' | 'rejetee', reponse: string) => Promise<void>;
  onOpenChat: () => void;
  onQuickEdit: () => void;
}> = ({ r, onTraiter, onOpenChat, onQuickEdit }) => {
  const [openAction, setOpenAction] = useState<'en_examen' | 'acceptee' | 'rejetee' | null>(null);
  const [reponse, setReponse] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (action: 'en_examen' | 'acceptee' | 'rejetee') => {
    if ((action === 'acceptee' || action === 'rejetee') && reponse.trim().length < 5) {
      alert('La réponse est obligatoire (5 caractères min).');
      return;
    }
    setLoading(true);
    try {
      await onTraiter(action, reponse);
      setOpenAction(null);
      setReponse('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border p-4 space-y-3 ${
      (r.nb_non_lus_admin || 0) > 0 ? 'border-blue-400 dark:border-blue-600 ring-1 ring-blue-200' : 'border-gray-200 dark:border-gray-700'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm text-gray-700 dark:text-gray-300">{r.matricule}</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {r.etudiant.nom} {r.etudiant.prenoms}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              ({r.etudiant.classe})
            </span>
            {r.etudiant.pole && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                {r.etudiant.pole}
              </span>
            )}
            {(r.nb_non_lus_admin || 0) > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-500 text-white font-bold animate-pulse">
                {r.nb_non_lus_admin} non lu{(r.nb_non_lus_admin || 0) > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="text-sm text-gray-700 dark:text-gray-300 mt-1">
            <span className="font-semibold">{r.type_reclamation === 'devoir' ? 'Devoir' : r.type_reclamation === 'examen' ? 'Examen' : 'Autre'}</span>
            {' • '}
            <span>{r.matiere_nom || '—'}</span>
            {r.semestre && <> • <span className="font-mono text-xs">{r.semestre}</span></>}
          </div>
          {(r.note_actuelle !== null || r.note_attendue !== null) && (
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {r.note_actuelle !== null && <>Note actuelle : <strong>{r.note_actuelle}</strong></>}
              {r.note_actuelle !== null && r.note_attendue !== null && ' → '}
              {r.note_attendue !== null && <>Attendue : <strong>{r.note_attendue}</strong></>}
            </div>
          )}
        </div>
        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${STATUT_BADGE[r.statut]}`}>
          {STATUT_LABEL[r.statut]}
        </span>
      </div>

      <div className="bg-gray-50 dark:bg-gray-900/30 rounded p-3 text-sm text-gray-800 dark:text-gray-200 italic line-clamp-2">
        « {r.motif} »
      </div>

      {r.reponse_scolarite && (
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-3 text-sm">
          <div className="text-xs font-semibold text-blue-800 dark:text-blue-200 mb-1">
            Dernière réponse scolarité {r.traite_par_nom && `(${r.traite_par_nom})`}
          </div>
          <div className="text-blue-900 dark:text-blue-100 line-clamp-2">{r.reponse_scolarite}</div>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-gray-400 flex-wrap gap-2">
        <div>
          Déposée le {fmtDate(r.date_creation)}
          {r.date_traitement && ` • Traitée le ${fmtDate(r.date_traitement)}`}
        </div>
        <div className="flex gap-1">
          {r.statut !== 'cloturee' && r.matiere_id && (r.type_reclamation === 'devoir' || r.type_reclamation === 'examen') && (
            <button
              onClick={onQuickEdit}
              className="flex items-center gap-1 px-2 py-1 rounded bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 hover:bg-green-100 font-bold border border-green-300 dark:border-green-700"
              title="Modifier la note + clôturer en un clic (avec scan)"
            >
              ⚡ Action rapide
            </button>
          )}
          <button
            onClick={onOpenChat}
            className="flex items-center gap-1 px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 font-medium"
          >
            💬 {r.nb_messages || 0} message{(r.nb_messages || 0) > 1 ? 's' : ''}
          </button>
        </div>
      </div>

      {r.statut !== 'acceptee' && r.statut !== 'rejetee' && r.statut !== 'cloturee' && (
        <div>
          {openAction ? (
            <div className="space-y-2">
              {(openAction === 'acceptee' || openAction === 'rejetee') && (
                <textarea
                  value={reponse}
                  onChange={(e) => setReponse(e.target.value)}
                  rows={2}
                  placeholder={
                    openAction === 'acceptee'
                      ? 'Réponse à l\'étudiant (ex: la note a été corrigée à 14/20)...'
                      : 'Motif du rejet...'
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => handleSubmit(openAction)}
                  disabled={loading}
                  className={`flex-1 py-2 text-sm font-medium rounded ${
                    openAction === 'acceptee' ? 'bg-green-600 hover:bg-green-700 text-white'
                      : openAction === 'rejetee' ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-orange-600 hover:bg-orange-700 text-white'
                  }`}
                >
                  {loading ? 'Envoi...' : `Confirmer ${openAction === 'en_examen' ? 'mise en examen' : openAction === 'acceptee' ? 'acceptation' : 'rejet'}`}
                </button>
                <button
                  onClick={() => { setOpenAction(null); setReponse(''); }}
                  className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded"
                >
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              {r.statut === 'deposee' && (
                <button
                  onClick={() => setOpenAction('en_examen')}
                  className="flex-1 py-2 text-sm bg-orange-100 dark:bg-orange-900/20 hover:bg-orange-200 text-orange-700 dark:text-orange-300 rounded font-medium"
                >
                  🔍 En examen
                </button>
              )}
              <button
                onClick={() => setOpenAction('acceptee')}
                className="flex-1 py-2 text-sm bg-green-100 dark:bg-green-900/20 hover:bg-green-200 text-green-700 dark:text-green-300 rounded font-medium"
              >
                ✓ Accepter
              </button>
              <button
                onClick={() => setOpenAction('rejetee')}
                className="flex-1 py-2 text-sm bg-red-100 dark:bg-red-900/20 hover:bg-red-200 text-red-700 dark:text-red-300 rounded font-medium"
              >
                ✗ Rejeter
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const POLES = ['Polytechnique', 'Commerce', 'Juridique'];
const NIVEAUX = ['L1', 'L2', 'L3', 'M1', 'M2'];

export const Reclamations: React.FC = () => {
  const [statut, setStatut] = useState('');
  const [pole, setPole] = useState('');
  const [niveau, setNiveau] = useState('');
  const [classe, setClasse] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [semestreParite, setSemestreParite] = useState<'' | 'pair' | 'impair'>('');
  const [typeSession, setTypeSession] = useState<'' | 'ordinaire' | 'rattrapage'>('');
  const [chatRec, setChatRec] = useState<ReclamationAdminItem | null>(null);
  const [quickEditId, setQuickEditId] = useState<number | null>(null);

  const { data, isLoading, refetch } = useListReclamationsAdminQuery({
    statut: statut || undefined,
    pole: pole || undefined,
    niveau: niveau || undefined,
    classe: classe || undefined,
    search: search || undefined,
    semestre_parite: semestreParite || undefined,
    type_session: typeSession || undefined,
  });
  const [traiterMut] = useTraiterReclamationMutation();

  const traiter = async (id: number, action: 'en_examen' | 'acceptee' | 'rejetee', reponse: string) => {
    try {
      await traiterMut({ id, action, reponse }).unwrap();
      refetch();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  const items = data?.results || [];
  const kpis = data?.kpis;
  const parPole = data?.par_pole || [];
  const restriction = data?.restriction_pole;

  // Pôles disponibles dans le dropdown : restriction si l'utilisateur est limité
  const polesDispo = useMemo(() => {
    if (restriction?.restreint && restriction.poles_autorises.length > 0) {
      return restriction.poles_autorises;
    }
    return POLES;
  }, [restriction]);

  // Si un seul pôle autorisé, on l'auto-sélectionne et on figera le dropdown
  const polesAutoFige = restriction?.restreint && restriction.poles_autorises.length === 1
    ? restriction.poles_autorises[0]
    : null;

  useEffect(() => {
    if (polesAutoFige && pole !== polesAutoFige) {
      setPole(polesAutoFige);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polesAutoFige]);

  const classesDispo = useMemo(() => {
    return Array.from(new Set(items.map((i) => i.etudiant.classe).filter(Boolean))).sort();
  }, [items]);

  const handleExport = () => {
    const usp = new URLSearchParams();
    if (statut) usp.set('statut', statut);
    if (pole) usp.set('pole', pole);
    if (niveau) usp.set('niveau', niveau);
    if (classe) usp.set('classe', classe);
    const token = localStorage.getItem('access_token');
    fetch(`/api/portail/admin/reclamations/export/?${usp.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `reclamations_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <div className="space-y-4">
      {chatRec && <ConversationModal reclamation={chatRec} onClose={() => { setChatRec(null); refetch(); }} />}
      {quickEditId !== null && (
        <QuickEditModal
          reclamationId={quickEditId}
          onClose={() => setQuickEditId(null)}
          onApplied={() => refetch()}
        />
      )}

      {/* Bandeau restriction de pôle */}
      {restriction?.restreint && (
        <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-300 dark:border-indigo-700 rounded-lg p-3 text-sm flex items-center gap-2">
          <span className="text-lg">🔐</span>
          <div className="flex-1">
            <span className="font-semibold text-indigo-800 dark:text-indigo-200">Accès limité — </span>
            <span className="text-indigo-700 dark:text-indigo-300">
              Vous voyez uniquement les réclamations du pôle{restriction.poles_autorises.length > 1 ? 's' : ''} :
              {' '}
              {restriction.poles_autorises.map((p, i) => (
                <span key={p} className="font-bold">
                  {p}{i < restriction.poles_autorises.length - 1 ? ', ' : ''}
                </span>
              ))}
            </span>
          </div>
        </div>
      )}

      {/* KPIs */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <KpiCard label="Total" value={kpis.total} color="gray" icon="📋" active={statut === ''} onClick={() => setStatut('')} />
          <KpiCard label="Déposées" value={kpis.deposees} color="blue" icon="⏳" active={statut === 'deposee'} onClick={() => setStatut(statut === 'deposee' ? '' : 'deposee')} />
          <KpiCard label="En examen" value={kpis.en_examen} color="orange" icon="🔍" active={statut === 'en_examen'} onClick={() => setStatut(statut === 'en_examen' ? '' : 'en_examen')} />
          <KpiCard label="Acceptées" value={kpis.acceptees} color="green" icon="✓" active={statut === 'acceptee'} onClick={() => setStatut(statut === 'acceptee' ? '' : 'acceptee')} />
          <KpiCard label="Rejetées" value={kpis.rejetees} color="red" icon="✗" active={statut === 'rejetee'} onClick={() => setStatut(statut === 'rejetee' ? '' : 'rejetee')} />
          <KpiCard label="Clôturées" value={kpis.cloturees} color="gray" icon="🔒" active={statut === 'cloturee'} onClick={() => setStatut(statut === 'cloturee' ? '' : 'cloturee')} />
          <KpiCard label="Non lus" value={kpis.avec_messages_non_lus} color="purple" icon="🔔" />
        </div>
      )}

      {/* Répartition par pôle (cachée si l'utilisateur n'a accès qu'à un seul pôle) */}
      {parPole.length > 0 && !polesAutoFige && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Répartition par pôle</div>
          <div className="flex flex-wrap gap-2">
            {parPole.map((p) => (
              <button
                key={p.pole}
                onClick={() => setPole(pole === p.pole ? '' : p.pole)}
                className={`px-3 py-1 text-sm rounded-full border ${
                  pole === p.pole
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                }`}
              >
                {p.pole} <span className="ml-1 font-bold">{p.nb}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filtres détaillés */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
              Pôle {polesAutoFige && <span className="text-indigo-600 dark:text-indigo-400">🔐</span>}
            </label>
            <select
              value={pole}
              onChange={(e) => setPole(e.target.value)}
              disabled={!!polesAutoFige}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {!polesAutoFige && <option value="">{restriction?.restreint ? 'Mes pôles' : 'Tous pôles'}</option>}
              {polesDispo.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Niveau</label>
            <select
              value={niveau}
              onChange={(e) => setNiveau(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Tous niveaux</option>
              {NIVEAUX.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Classe</label>
            <select
              value={classe}
              onChange={(e) => setClasse(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Toutes classes</option>
              {classesDispo.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="lg:col-span-2">
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Recherche</label>
            <div className="flex gap-1">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)}
                placeholder="Matricule, matière, motif..."
                className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <button onClick={() => setSearch(searchInput)} className="px-2 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded">🔍</button>
            </div>
          </div>
        </div>

        {/* Filtres : parité semestre + type session */}
        <div className="flex flex-wrap gap-4 items-center border-t border-gray-200 dark:border-gray-700 pt-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Semestre :</span>
            {([
              { k: '', l: 'Tous' },
              { k: 'impair', l: 'Impair (S1, S3, S5)' },
              { k: 'pair', l: 'Pair (S2, S4, S6)' },
            ] as const).map((opt) => (
              <button
                key={opt.k || 'all'}
                onClick={() => setSemestreParite(opt.k)}
                className={`px-2.5 py-1 text-xs rounded font-medium border ${
                  semestreParite === opt.k
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                }`}
              >{opt.l}</button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Session :</span>
            {([
              { k: '', l: 'Toutes' },
              { k: 'ordinaire', l: 'Ordinaire' },
              { k: 'rattrapage', l: 'Rattrapage' },
            ] as const).map((opt) => (
              <button
                key={opt.k || 'all'}
                onClick={() => setTypeSession(opt.k)}
                className={`px-2.5 py-1 text-xs rounded font-medium border ${
                  typeSession === opt.k
                    ? (opt.k === 'rattrapage' ? 'bg-orange-600 text-white border-orange-600' : 'bg-blue-600 text-white border-blue-600')
                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                }`}
              >{opt.l}</button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {STATUTS.map((s) => (
            <button
              key={s.key}
              onClick={() => setStatut(s.key)}
              className={`px-3 py-1.5 text-xs rounded-full border ${
                statut === s.key
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
              }`}
            >{s.label}</button>
          ))}
          <div className="ml-auto flex gap-2">
            {(pole || niveau || classe || search || statut || semestreParite || typeSession) && (
              <button
                onClick={() => {
                  setPole(''); setNiveau(''); setClasse(''); setSearch(''); setSearchInput('');
                  setStatut(''); setSemestreParite(''); setTypeSession('');
                }}
                className="text-xs px-3 py-1.5 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded"
              >✕ Effacer filtres</button>
            )}
            <button
              onClick={handleExport}
              className="text-xs px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded"
            >📥 Export CSV</button>
            <button
              onClick={() => refetch()}
              className="text-xs px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded"
            >🔄</button>
          </div>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {isLoading ? 'Chargement...' : `${items.length} réclamation${items.length > 1 ? 's' : ''} affichée${items.length > 1 ? 's' : ''}`}
        </div>
      </div>

      {/* Liste */}
      <div className="space-y-3">
        {items.map((r) => (
          <ReclamationCard
            key={r.id}
            r={r}
            onTraiter={(action, reponse) => traiter(r.id, action, reponse)}
            onOpenChat={() => setChatRec(r)}
            onQuickEdit={() => setQuickEditId(r.id)}
          />
        ))}
        {items.length === 0 && !isLoading && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-12 text-center text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
            Aucune réclamation pour ces filtres.
          </div>
        )}
      </div>
    </div>
  );
};
